import { Button, Fab } from '@/components/elements/forms/buttons';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import { ROUTE_BUILDER } from '@/constants/pages';
import { buildSerpOrSokLink } from '@/features/mega-menu/helpers';
import { classnames, loggedUser } from '@/helpers';
import { locationSlug } from '@/helpers/urlTs';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import { isDealsSearch, Position, positionSchema, SearchType } from '@/hooks/search/useSearch.helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s, _t } from '@/locale';
import { autocompleteService } from '@/services';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import KeywordAutoSuggest from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest';
import useKeywordAutoSuggestManager from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.hooks';
import { KeywordSelection } from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.type';
import LocationAutosuggest from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest';
import useLocationAutoSuggestManager from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.hooks';
import { LocationSuggestion } from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.types';
import {
  AutoSuggestInputComponent,
  KeywordAutoSuggestSectionTitle,
  KeywordSuggestionItem,
  LocationAutoSuggestSectionTitle,
  LocationSuggestionItem,
} from '../../components/AutoSuggest/shared/templates';
import { AutoSuggestInputTheme } from '../../components/AutoSuggest/shared/types';
import { SEARCH_CONTEXT } from '../../constants/tracking';
import { SearchContext } from '../../types/tracking';

import FakeSearchInput from '@/components/modules/mobile/FakeSearchInput';
import Toggle from '@/features/deals/components/Toggle';
import { buildDealKeywordSuggestionTarget } from '@/features/deals/hooks/useDealsSearch.helpers';
import { useSmtOnlyToggle } from '@/features/deals/hooks/useSmtOnlyToggle';
import { useIsInSearchHeader } from '@/features/search-header/SearchHeader.context';
import {
  clearLastLocationAnonymousCookie,
  clearLastLocationSuggestionFromLocalStorage,
} from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';
import { FakeSearchInputContentWithoutManager } from '../SearchForm/SearchForm';

const baseTranslationKey = 'features.searchV2.modules.FakeSearchForm';

const AUTOGUSGEST_THEME: AutoSuggestInputTheme = {
  container: 'relative w-full',
  suggestionsContainer: 'absolute w-full right-0 left-0 max-h-[550px]',
  suggestion: 'cursor-pointer hover:bg-surface_l4 rounded-md space-x-md',
  suggestionHighlighted: 'bg-surface_l4',
  suggestionsList: 'last:mb-md',
  suggestionsContainerOpen:
    'z-[1001] bg-white border border-border_bold bg-white px-lg pt-lg mt-xs rounded-md max-h-[550px] overflow-y-auto overscroll-none',
};

export type OnLocationUpdate = (params: { location: string; lat?: string; lon?: string }) => void;
export type OnKeywordUpdate = (keyword: string) => void;

export type FakeSearchFormRef = {
  setKeyword: (keyword: string) => void;
  setLocation: (params: { location: string; lat?: string; lon?: string }) => void;
};

type FakeSearchFormProps = {
  type?: SearchType;
  onKeywordUpdate?: OnKeywordUpdate;
  onLocationUpdate?: OnLocationUpdate;
};

const FakeSearchFormDesktop = forwardRef<FakeSearchFormRef, FakeSearchFormProps>((props, ref) => {
  const [locationId, setLocationId] = useState<string>(undefined);
  const [position, setPosition] = useState<Position>(undefined);
  const [searchContext, setSearchContext] = useState<SearchContext>('home_page_free_search');
  const [isFocused, setIsFocused] = useState<'keyword' | 'location'>();
  const { isSmtOnlySearch, setIsSmtOnlySearch } = useSmtOnlyToggle(props.type);

  const history = useHistory();
  const location = useLocation();

  const keywordAutoSuggestManager = useKeywordAutoSuggestManager({
    initialState: {
      keyword: '',
      suggestions: [],
      storedSuggestions: [],
      newServices: [],
      hasHistory: false,
      showSuggestions: true,
    },
    searchType: props.type,
    onSelectKeywordSuggestion: handleOnSelectKeywordSuggestion,
  });

  const locationAutoSuggestManager = useLocationAutoSuggestManager({
    initialState: {
      locationValue: '',
      hasHistory: false,
      suggestions: [],
      storedSuggestions: [],
      showSuggestions: true,
      isFetchingUserPosition: false,
    },
    onSelectLocationSuggestion: handleOnUpdateLocation,
  });
  const keywordInputRef = useRef(null);
  const locationInputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    setKeyword: (keyword: string) => {
      keywordAutoSuggestManager.onChange(null, { newValue: keyword });
      // Focus on location input after setting keyword
      setTimeout(() => {
        locationInputRef?.current?.focus?.();
      }, 0);
    },
    setLocation: (params: { location: string; lat?: string; lon?: string }) => {
      locationAutoSuggestManager.onChange({ target: { value: params.location } });
      locationAutoSuggestManager.onSuggestionsClearRequested({});

      setLocationId(undefined);
      setPosition(undefined);

      if (params.lat && params.lon) {
        const pos = `${params.lat}-${params.lon}`;
        if (positionSchema.safeParse(pos).success) {
          setPosition(pos);
        }
      }
    },
  }));

  function handleOnUpdateLocation(params) {
    // reset previous state
    setLocationId(undefined);
    setPosition(undefined);

    if (params.locationId) {
      setLocationId(params.locationId);
    }

    const position = `${params['lat']}-${params['lon']}`;

    if (positionSchema.safeParse(position).success) {
      setPosition(position);
    }

    if (props.onLocationUpdate) {
      props.onLocationUpdate({ location: params.location, lat: params.lat, lon: params.lon });
    }
  }

  function handleOnSelectKeywordSuggestion(suggestion: KeywordSelection) {
    setSearchContext('home_page_search_autocomplete');

    if (suggestion.type === 'deal') {
      const target = buildDealKeywordSuggestionTarget(suggestion, locationAutoSuggestManager.location || undefined);
      history.push(target);
      return;
    }

    if (suggestion.type === 'brand_deal') {
      const target = ROUTE_BUILDER.brandDealsSerp(suggestion.slug, locationAutoSuggestManager.location || undefined);
      history.push(target);
      return;
    }
    /**
     * Make autosuggest keyword trigger auto focus on location input if empty
     */
    if (!locationAutoSuggestManager.location) {
      locationInputRef?.current?.focus?.();
    }
  }

  function handleOnClearKeyword() {
    keywordInputRef?.current?.focus?.();
    keywordAutoSuggestManager.onChange(null, { newValue: '' });
  }

  function handleClearLocation() {
    locationInputRef?.current?.focus?.();
    setLocationId(undefined);
    setPosition(undefined);
    locationAutoSuggestManager.onChange({ target: { value: '' } });
    setLocationId(undefined);
    setPosition(undefined);
  }

  function handleOnSearchClick() {
    if (keywordAutoSuggestManager.keyword) {
      keywordAutoSuggestManager.saveKeywordToHistory(keywordAutoSuggestManager.keyword);
    }

    const state = {
      keyword: keywordAutoSuggestManager.keyword || DEFAULT_SEARCH_KEYWORD,
      location: locationAutoSuggestManager.location || DEFAULT_SEARCH_LOCATION,
    };

    const query = new URLSearchParams(location.search);

    if (position && locationSlug(state.location) === DEFAULT_MY_LOCATION_KEYWORD) {
      const [lat, lon] = position.split('-');
      query.set('lat', lat);
      query.set('lon', lon);
    }

    const { pathname, searchString } = buildSerpOrSokLink(state.keyword, state.location, props.type, query.toString());

    clearLastLocationSuggestionFromLocalStorage();

    if (loggedUser()) {
      clearLastLocationAnonymousCookie();
    }

    history.push({ pathname, search: searchString, state: { keywordSubmitted: true, searchContext } });
  }

  function handleRemoveLocationHistorySuggestion(suggestion: LocationSuggestion) {
    autocompleteService.deleteLocationSuggestion(suggestion.label);
    locationAutoSuggestManager.onClearHistorySuggestion(suggestion);
    clearLastLocationSuggestionFromLocalStorage();
  }

  function handleOnAutoSuggestKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter' || event.keyCode === 13) {
      handleOnSearchClick();
    }
  }

  function handleOnFocus(type: 'keyword' | 'location', cb: () => void) {
    setIsFocused(type);
    cb?.();
  }

  function handleOnBlur(cb: () => void) {
    setIsFocused(undefined);
    cb?.();
  }

  /**
   * If both keyword and location are selected, trigger search
   */
  useEffect(() => {
    if (
      locationAutoSuggestManager.location &&
      keywordAutoSuggestManager.keyword &&
      searchContext === 'home_page_search_autocomplete' &&
      isFocused === undefined
    ) {
      handleOnSearchClick();
    }
  }, [locationAutoSuggestManager.location, keywordAutoSuggestManager.keyword]);

  const inputSharedClassNames = classnames(
    'border-border_regular group-hover:border-border_bold group-hover:bg-surface_l4 h-[52px]',
    isFocused ? 'bg-surface_l4' : 'bg-surface_l2',
  );

  const hasKeywordInput = Boolean(keywordAutoSuggestManager.keyword);
  const hasLocationInput = Boolean(locationAutoSuggestManager.location);
  const hasDirtyFields = Boolean(hasKeywordInput || hasLocationInput);

  return (
    <div className="group flex items-stretch">
      <div className="grid w-full grid-cols-2">
        <div className="relative">
          <KeywordAutoSuggest
            inputRef={keywordInputRef}
            {...keywordAutoSuggestManager}
            onKeyDown={handleOnAutoSuggestKeyDown}
            theme={AUTOGUSGEST_THEME}
            renderInputComponent={({ key, ...inputProps }) => (
              <AutoSuggestInputComponent
                key={key}
                inputProps={{
                  ...inputProps,
                  onFocus: () => handleOnFocus('keyword', inputProps.onFocus),
                  onBlur: () => handleOnBlur(inputProps.onBlur),
                  /*
                   * Change placeholder with title when input is focused
                   */
                  placeholder: !isFocused ? inputProps.title : inputProps.placeholder,
                }}
                inputRef={keywordInputRef}
                isFocused={isFocused === 'keyword'}
                onClear={handleOnClearKeyword}
                leftSlot={<Icon variant={props.type === 'deals' ? 'search-deal' : 'search'} size="sm" />}
                className={classnames(inputSharedClassNames, 'rounded-bl-full rounded-tl-full border-r-0')}
              />
            )}
            renderSuggestion={(suggestion, options) => {
              return (
                <KeywordSuggestionItem
                  suggestion={suggestion}
                  options={options}
                  onRemoveHistorySuggestion={keywordAutoSuggestManager.onClearHistorySuggestion}
                />
              );
            }}
            renderSectionTitle={(section) => <KeywordAutoSuggestSectionTitle {...section} />}
            renderSuggestionsContainer={({ containerProps, children }) => {
              return (
                <div {...containerProps}>
                  {children}
                  {/* Render deals toggle inside suggestions container */}
                  {isDealsSearch(props.type) && children !== null && (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      style={{
                        position: 'sticky',
                        bottom: 0,
                        right: 0,
                        left: 0,
                        marginLeft: -20,
                        marginRight: -20,
                      }}
                      className="bg-surface_l2 border-border_regular border-t">
                      <div className="py-lg px-xl flex items-center justify-between">
                        <span className="text-fg_primary text-l">{_t('searchSistaminutenOnly')}</span>
                        <Toggle checked={isSmtOnlySearch} onChange={() => setIsSmtOnlySearch((prev) => !prev)} />
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
          />
          {/* Seperator */}
          {!isFocused && <div className="bg-border_regular bottom-xs top-xs absolute right-0 w-[1px]"></div>}
        </div>
        <LocationAutosuggest
          inputRef={locationInputRef}
          {...locationAutoSuggestManager}
          onKeyDown={(event) => handleOnAutoSuggestKeyDown(event)}
          theme={AUTOGUSGEST_THEME}
          renderInputComponent={({ key, ...inputProps }) => (
            <AutoSuggestInputComponent
              key={key}
              inputProps={{
                ...inputProps,
                placeholder: isFocused === 'location' ? inputProps.placeholder : _s('where'),
                onFocus: () => handleOnFocus('location', inputProps.onFocus),
                onBlur: () => handleOnBlur(inputProps.onBlur),
              }}
              inputRef={locationInputRef}
              isFocused={isFocused === 'location'}
              onClear={handleClearLocation}
              leftSlot={
                locationAutoSuggestManager.isFetchingUserPosition ? (
                  <LoadingIcon />
                ) : (
                  <Icon variant="location" size="sm" />
                )
              }
              className={classnames(inputSharedClassNames, 'border-l-0 border-r-0')}
            />
          )}
          renderSuggestion={(suggestion, options) => (
            <LocationSuggestionItem
              suggestion={suggestion}
              options={options}
              onRemoveHistorySuggestion={handleRemoveLocationHistorySuggestion}
            />
          )}
          renderSectionTitle={(section) => <LocationAutoSuggestSectionTitle {...section} />}
        />
      </div>

      <div
        className={classnames(
          inputSharedClassNames,
          'px-xs flex h-auto items-center justify-center rounded-br-full rounded-tr-full border border-l-0',
        )}>
        {hasDirtyFields && (
          <Button
            className="rounded-[1000px]"
            leftIcon={<Icon variant="search" size="xs" />}
            variant="primary"
            size="sm"
            onClick={handleOnSearchClick}>
            {_s('search')}
          </Button>
        )}
        {!hasDirtyFields && (
          <Fab
            onClick={handleOnSearchClick}
            size="sm"
            variant="primary"
            style={{ width: 40, height: 40 }}
            icon={
              <Icon
                variant="search"
                size="xs"
                style={{ width: 16, height: 16, filter: 'invert(1)' }}
                aria-label={_s('search')}
              />
            }
          />
        )}
      </div>
    </div>
  );
});

const FakeSearchFormMobile = forwardRef<FakeSearchFormRef, FakeSearchFormProps>((props, ref) => {
  const history = useHistory();
  const [locationParams, setLocationParams] = useState<{ location: string; lat?: string; lon?: string } | undefined>(
    undefined,
  );
  const isInSearchHeader = useIsInSearchHeader();

  useImperativeHandle(ref, () => ({
    setKeyword: (keyword: string) => {
      // Mobile version navigates to search page with initial keyword in state
      history.push({
        pathname: '/search',
        state: {
          fromLandingPage: true,
          searchContext: SEARCH_CONTEXT.HOME_PAGE_FREE_SEARCH,
          searchType: props.type,
          initialKeyword: keyword,
        },
      });
    },
    setLocation: (params) => {
      setLocationParams(params);
    },
  }));

  const keywordPlaceholder = _s(`${baseTranslationKey}.keywordInput.placeholder`);
  const locationPlaceHolder = locationParams?.location || undefined;

  return (
    <FakeSearchInput
      isLoading={false}
      onClick={() => {
        const query = new URLSearchParams();
        if (locationParams?.lat && locationParams?.lon) {
          query.set('lat', locationParams.lat);
          query.set('lon', locationParams.lon);
        }
        history.push({
          pathname: '/search',
          state: {
            fromLandingPage: true,
            searchContext: SEARCH_CONTEXT.HOME_PAGE_FREE_SEARCH,
            searchType: props.type,
            ...(locationParams?.location ? { location: locationParams.location } : {}),
          },
          ...(query.toString() ? { search: query.toString() } : {}),
        });
      }}
      valid={false}
      icon={props.type === 'deals' ? 'search-deal' : 'search'}
      className={classnames('bg-surface_l2', !isInSearchHeader && 'drop-shadow-md')}
      onClear={() => null}
      content={<FakeSearchInputContentWithoutManager location={locationPlaceHolder} keyword={keywordPlaceholder} />}
      size="sm"
    />
  );
});

FakeSearchFormMobile.displayName = 'FakeSearchFormMobile';
FakeSearchFormDesktop.displayName = 'FakeSearchFormDesktop';

const FakeSearchForm = forwardRef<FakeSearchFormRef, FakeSearchFormProps>(
  ({ type = 'default', onKeywordUpdate, onLocationUpdate }, ref) => {
    const { isMobileView } = useMobileView();

    if (isMobileView)
      return (
        <FakeSearchFormMobile
          ref={ref}
          type={type}
          onKeywordUpdate={onKeywordUpdate}
          onLocationUpdate={onLocationUpdate}
        />
      );

    return (
      <FakeSearchFormDesktop
        ref={ref}
        type={type}
        onKeywordUpdate={onKeywordUpdate}
        onLocationUpdate={onLocationUpdate}
      />
    );
  },
);
export default FakeSearchForm;
